/**
 * Player Progress Service - Git-like branching system for cartridge instances
 * Tracks individual player progress through cartridge story branches
 */

import { databaseService } from './databaseService';
import { NarrativeGitService, NarrativeBranch, NarrativeCommit } from './narrativeGitService';

export interface PlayerProgressBranch extends NarrativeBranch {}

export interface ProgressCommit {
  commitHash: string;
  message: string;
  choicesMade: string[];
  storySegmentId: string;
  gameState: any; // Serialized game state
  metadata: {
    karma: number;
    experience: number;
    health: number;
    spiritualEnergy: number;
    location: string;
  };
  timestamp: Date;
}

class PlayerProgressService {
  private static instance: PlayerProgressService;
  private narrativeGitService: NarrativeGitService;
  
  static getInstance(): PlayerProgressService {
    if (!PlayerProgressService.instance) {
      PlayerProgressService.instance = new PlayerProgressService();
    }
    return PlayerProgressService.instance;
  }

  private constructor() {
    this.narrativeGitService = new NarrativeGitService(databaseService);
  }

  /**
   * Create a new progress branch for a player's cartridge instance
   */
  async createPlayerBranch(
    userId: string, 
    cartridgeId: string, 
    cartridgeName: string
  ): Promise<NarrativeBranch> {
    try {
      const instanceId = this.generateInstanceId();
      // The branch name format is now handled by NarrativeGitService
      const branch = await this.narrativeGitService.createPlayerBranch(
        userId,
        instanceId,
        cartridgeId
      );
      
      console.log(`[PlayerProgressService] Created branch: ${branch.branchName}`);
      return branch;
    } catch (error) {
      console.error('[PlayerProgressService] Error creating player branch:', error);
      throw error;
    }
  }

  /**
   * Commit player progress to their branch
   */
  async commitProgress(
    branchId: string, // This is now the branchName from NarrativeGitService
    choicesMade: string[],
    storySegmentId: string,
    gameState: any,
    metadata: ProgressCommit['metadata']
  ): Promise<NarrativeCommit> {
    try {
      // Extract instanceId and cartridgeId from branchId (branchName)
      const parts = branchId.split('/');
      const cartridgeId = parts[1];
      const instanceId = parts[2];
      const playerId = parts[3];

      const commit = await this.narrativeGitService.commitStoryState(
        playerId,
        instanceId,
        cartridgeId,
        gameState.currentStorySegment, // Assuming gameState contains currentStorySegment
        choicesMade.map(choice => ({ choice, timestamp: new Date() })), // Convert to PlayerChoice[]
        gameState, // Pass full gameState as worldState
        metadata.location // Location from metadata
      );
      
      console.log(`[PlayerProgressService] Committed progress: ${commit.message}`);
      return commit;
    } catch (error) {
      console.error('[PlayerProgressService] Error committing progress:', error);
      throw error;
    }
  }

  /**
   * Get player's progress branch for a cartridge
   */
  async getPlayerBranch(userId: string, cartridgeId: string, instanceId: string): Promise<NarrativeBranch | null> {
    try {
      return this.narrativeGitService.getPlayerBranch(userId, instanceId, cartridgeId);
    } catch (error) {
      console.error('[PlayerProgressService] Error getting player branch:', error);
      return null;
    }
  }

  /**
   * Get all commits for a branch
   */
  async getBranchCommits(branchName: string): Promise<NarrativeCommit[]> {
    try {
      const branch = await this.narrativeGitService.getNarrativeBranch(branchName);
      return branch ? branch.commits : [];
    } catch (error) {
      console.error('[PlayerProgressService] Error getting branch commits:', error);
      return [];
    }
  }

  /**
   * Restore player progress from a specific commit
   */
  async restoreFromCommit(commitHash: string): Promise<any> {
    try {
      // This needs to be implemented in NarrativeGitService or fetched directly
      // For now, we'll return a placeholder
      console.warn('[PlayerProgressService] restoreFromCommit not fully implemented');
      return {};
    } catch (error) {
      console.error('[PlayerProgressService] Error restoring from commit:', error);
      throw error;
    }
  }

  /**
   * Get player's latest progress
   */
  async getLatestProgress(userId: string, cartridgeId: string, instanceId: string): Promise<any> {
    try {
      const branch = await this.getPlayerBranch(userId, cartridgeId, instanceId);
      if (!branch || branch.commits.length === 0) return null;

      const latestCommit = branch.commits.sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime())[0];
      return latestCommit.worldState; // Assuming worldState contains the full game state
    } catch (error) {
      console.error('[PlayerProgressService] Error getting latest progress:', error);
      return null;
    }
  }

  // Private helper methods

  private generateInstanceId(): string {
    return Math.random().toString(36).substr(2, 9) + Date.now().toString(36);
  }

  private generateCommitHash(branchId: string, storySegmentId: string, choices: string[]): string {
    const content = `${branchId}_${storySegmentId}_${choices.join('|')}_${Date.now()}`;
    return this.simpleHash(content);
  }

  private simpleHash(str: string): string {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      const char = str.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // Convert to 32-bit integer
    }
    return Math.abs(hash).toString(16);
  }
}

// Export the singleton instance
export const playerProgressService = PlayerProgressService.getInstance();
